import * as M from '@mantine/core';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { telecomFeePlanOptions } from '../../../../../../shared/constants/telecomFeePlans';
import type { ICalculatorFormValues, ICalculatorMode } from '../../../../../../shared/types/device';
import useGetTelecomPlans from '../hooks/useGetTelecomPlans';
import {
  calculatorItems,
  calculatorModeOptions,
  calculatorResultKeys,
  defaultCalculatorMode,
  getCalculatorSummary,
} from '../utils/calculator';

interface ICalculatorProps {
  opened: boolean;
  onClose: () => void;
}

const defaultCalculatorValues: ICalculatorFormValues = {
  serverConnection: 0,
  heartbeatCycle: 7,
  dataCollectionCount: 0,
  arrayDataCollectionCount: 0,
  arrayElementCount: 0,
  dataTransferCycle: 0,
  alarmCount: 0,
  telecomPlan: telecomFeePlanOptions[0]?.value,
  serverConnectionUsage: 0,
  heartbeatUsage: 0,
  dataCollectionUsage: 0,
  arrayDataCollectionUsage: 0,
  arrayElementUsage: 0,
  dataTransferUsage: 0,
  alarmUsage: 0,
};

export default function Calculator(props: ICalculatorProps) {
  const { opened, onClose } = props;
  const [mode, setMode] = useState<ICalculatorMode>(defaultCalculatorMode);

  const { control, reset, setValue, watch } = useForm<ICalculatorFormValues>({
    defaultValues: defaultCalculatorValues,
  });

  const values = watch();
  const { data, isFetching, isLoading } = useGetTelecomPlans(values.telecomPlan);
  const summary = data ? getCalculatorSummary(values, mode, data) : null;

  useEffect(() => {
    if (!opened) {
      reset(defaultCalculatorValues);
      setMode(defaultCalculatorMode);
      return;
    }

    if (!summary) {
      return;
    }

    calculatorResultKeys.forEach((key) => {
      if (values[key] !== summary.activeUsageResults[key]) {
        setValue(key, summary.activeUsageResults[key], {
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    });
  }, [opened, reset, setValue, summary, values]);

  const onReset = () => {
    reset(defaultCalculatorValues);
  };

  return (
    <M.Modal opened={opened} onClose={onClose} title="데이터 사용량 계산기" centered size="xl">
      <M.Stack gap="lg" px="xs">
        <M.SegmentedControl
          fullWidth
          value={mode}
          onChange={(value) => setMode(value as ICalculatorMode)}
          data={calculatorModeOptions}
        />

        <M.Box>
          <M.Grid gutter="md" mb="sm">
            <M.Grid.Col span={3}>
              <M.Text c="#8a8a8a" size="xs" ta="center">
                항목
              </M.Text>
            </M.Grid.Col>
            <M.Grid.Col span={3}>
              <M.Text c="#8a8a8a" size="xs" ta="center">
                설명
              </M.Text>
            </M.Grid.Col>
            <M.Grid.Col span={3}>
              <M.Text c="#8a8a8a" size="xs" ta="center">
                입력 값
              </M.Text>
            </M.Grid.Col>
            <M.Grid.Col span={3}>
              <M.Text c="#8a8a8a" size="xs" ta="center">
                데이터 사용량
              </M.Text>
            </M.Grid.Col>
          </M.Grid>

          <M.Divider color="#dddddd" mb="md" />

          <M.Stack gap="md">
            {calculatorItems.map((item) => (
              <M.Grid key={item.key} align="center" gutter="md">
                <M.Grid.Col span={3}>
                  <M.Group gap={8} wrap="nowrap">
                    <M.Text fw={600} size="sm" c="#1e1e1e">
                      {item.label}
                    </M.Text>
                  </M.Group>
                </M.Grid.Col>

                <M.Grid.Col span={3}>
                  <M.Text size="xs" c="#1e1e1e" lineClamp={2}>
                    {item.helperText}
                  </M.Text>
                </M.Grid.Col>

                <M.Grid.Col span={3}>
                  <Controller
                    name={item.key}
                    control={control}
                    render={({ field }) => (
                      <M.NumberInput
                        hideControls
                        min={0}
                        thousandSeparator=","
                        value={field.value}
                        onChange={(value) => field.onChange(typeof value === 'number' ? value : 0)}
                        rightSection={
                          <M.Text c="gray.6" size="xs" pr={14}>
                            {item.unit}
                          </M.Text>
                        }
                        styles={{
                          input: {
                            height: 36,
                            paddingRight: 52,
                            textAlign: 'right',
                            color: '#444444',
                          },
                        }}
                      />
                    )}
                  />
                </M.Grid.Col>

                <M.Grid.Col span={3}>
                  <Controller
                    name={item.resultKey}
                    control={control}
                    render={({ field }) => (
                      <M.NumberInput
                        hideControls
                        readOnly
                        thousandSeparator=","
                        value={field.value}
                        rightSection={
                          <M.Text c="gray.6" size="xs" pr={14}>
                            Byte
                          </M.Text>
                        }
                        styles={{
                          input: {
                            height: 36,
                            paddingRight: 56,
                            textAlign: 'right',
                            color: '#444444',
                            border: '1px solid #dadada',
                            backgroundColor: '#fafafa',
                            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                          },
                        }}
                      />
                    )}
                  />
                </M.Grid.Col>
              </M.Grid>
            ))}
          </M.Stack>
        </M.Box>

        <M.Divider color="#dddddd" />

        <Controller
          name="telecomPlan"
          control={control}
          render={({ field }) => (
            <M.Select
              label="요금제"
              data={telecomFeePlanOptions}
              value={field.value ?? null}
              onChange={(value) => field.onChange(value ?? undefined)}
            />
          )}
        />

        <M.Stack gap="lg">
          {(isLoading || isFetching) && (
            <M.Text size="sm" c="dimmed">
              요금제 정보를 불러오는 중입니다.
            </M.Text>
          )}

          <M.Box>
            <M.Group justify="space-between" align="flex-end" gap="sm" mb={4}>
              <M.Text fw={700} size="sm">
                예상 요금(원)
              </M.Text>
              <M.Text size="xs" c="dimmed">
                *VAT 별도 금액(무제한은 VAT포함)
              </M.Text>
            </M.Group>
            <M.Paper radius="md" p="xs" bg="#f8f8f8" withBorder={false}>
              <M.Stack gap="sm">
                {summary?.estimatedCostItems.map((item) => (
                  <M.Group key={item.label} justify="space-between" align="center" px="md">
                    <M.Text size="sm" fw={500}>
                      {item.label}
                    </M.Text>
                    <M.Text size="sm" fw={500}>
                      <M.NumberFormatter
                        value={item.value}
                        thousandSeparator
                        prefix={item.prefix}
                        suffix={item.suffix}
                        decimalScale={item.fractionDigits}
                        fixedDecimalScale={typeof item.fractionDigits === 'number'}
                      />
                    </M.Text>
                  </M.Group>
                ))}
              </M.Stack>
            </M.Paper>
          </M.Box>

          <M.Box>
            <M.Text fw={700} size="sm" mb={4}>
              산출 기준(원)
            </M.Text>
            <M.Paper radius="md" p="xs" bg="#f8f8f8" withBorder={false}>
              <M.Stack gap="sm">
                {summary?.costBreakdownItems.map((item) => (
                  <M.Group key={item.label} justify="space-between" align="center" px="md">
                    <M.Text size="sm" fw={500}>
                      {item.label}
                    </M.Text>
                    <M.Text size="sm" fw={500}>
                      <M.NumberFormatter
                        value={item.value}
                        thousandSeparator
                        prefix={item.prefix}
                        suffix={item.suffix}
                        decimalScale={item.fractionDigits}
                        fixedDecimalScale={typeof item.fractionDigits === 'number'}
                      />
                    </M.Text>
                  </M.Group>
                ))}
              </M.Stack>
            </M.Paper>
          </M.Box>
        </M.Stack>

        <M.Group justify="center">
          <M.Button variant="default" type="button" onClick={onReset}>
            초기화
          </M.Button>
        </M.Group>
      </M.Stack>
    </M.Modal>
  );
}
